import { useCallback, useMemo, useState } from 'react';
import { documentsScopesEnabled } from '../../extensions/bundled/documents/documentsFlags';
import useWorkspaces from './useWorkspaces';
import { createWorkspace, moveToWorkspace, reorderWorkspaces } from './workspaceActions';

/**
 * The workspace half of the Documents sidebar (cards 5.2 / 5.3): whether
 * workspaces are on (the `documents.scopes` flag), the list, which modal is
 * open, and the switcher's drop / reorder handlers. Kept out of
 * DocumentsSidebar.js so that file stays layout.
 *
 * `atLimit`: the plan's synced-workspace limit (`/auth/usage`
 * limits.document_scopes; absent or null = unlimited) is reached — the New
 * workspace modal then shows the upgrade hint (creating still works locally).
 */
export default function useWorkspaceControls({ user }) {
  const workspacesOn = useMemo(() => documentsScopesEnabled(), []);
  const { workspaces } = useWorkspaces(workspacesOn);
  const [dialog, setDialog] = useState(null); // 'new' | 'manage' | null

  const limit = user?._usage?.limits?.document_scopes;
  const atLimit = typeof limit === 'number' && limit >= 0 && workspaces.length >= limit;

  const onMoveTree = useCallback((drag, choice) => {
    if (!drag?.node) return;
    const scopeId = choice.kind === 'workspace' ? choice.scopeId : null;
    moveToWorkspace(drag.node, drag.scope, scopeId, choice.label);
  }, []);

  const onReorder = useCallback((ids) => reorderWorkspaces(ids), []);

  const create = useCallback(async (fields) => {
    const ws = await createWorkspace(fields);
    if (ws) setDialog(null);
    return !!ws;
  }, []);

  return {
    workspacesOn,
    workspaces,
    dialog,
    setDialog,
    atLimit,
    onMoveTree,
    onReorder,
    create,
  };
}
